import { IcAirConditioning, IcServer, IcServers, IcStormShield, IcVentilation, IcWebRelay } from '@assets/icons/32x32';
import { chakra } from '@chakra-ui/react';
import { colors } from '@theme/foundations/colors';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { HealthStatusEnum } from '@/types/sensor/sensors';

import Gate from '../../schematic/Gate';
import SquareInfo from '../../schematic/SquareInfo';
import TrigramInfo from '../../schematic/TrigramInfo';
import { commonHolopticsI18nPath } from './HolopticsSchematic';

const Text = chakra('text');

export default function HolopticsBaieSchematic() {
  const { formatMessage } = useIntl();
  const textRef = useRef<SVGTextElement | null>(null);
  const [bbox, setBbox] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (textRef.current) {
      const box = textRef.current.getBBox();
      setBbox(box);
    }
  }, []);

  return (
    <svg width="1219" height="519" viewBox="0 0 1219 519" fill="none">
      {bbox && (
        <rect
          x={bbox.x - 8}
          y={bbox.y - 3}
          width={bbox.width + 16}
          height={bbox.height + 7}
          fill={colors.neutral['600'].value}
        />
      )}
      <Text
        ref={textRef}
        y="74"
        x="440"
        textAnchor="end"
        fontWeight="medium"
        fill={colors.neutral['300'].value}
        style={{ textTransform: 'uppercase' }}
      >
        {formatMessage({ id: `${commonHolopticsI18nPath}.webRelayBaie` })}
      </Text>
      <g clipPath="url(#clip0_17728_68870)">
        <rect y="43" width="479" height="402" fill={colors.neutral['600'].value} />
        <rect width="364" height="78" transform="translate(59 278)" fill={colors.neutral['700'].value} />
        <path
          d="M118.25 317H395.75"
          //TODO isConnectedVentilation "M118.25 317H394" when open
          stroke={colors.neutralBlue['800'].value}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="bevel"
          strokeDasharray="1,3"
        />

        {/* Stormshield */}
        <SquareInfo
          x={75}
          y={102}
          name={formatMessage({ id: `${commonHolopticsI18nPath}.stormShield` })}
          isConnected
          isPresent
          icon={IcStormShield}
          status={HealthStatusEnum.HEALTHY}
        />
        <TrigramInfo trigram="STS" x={123} y={115.5} status={HealthStatusEnum.HEALTHY} />
        <TrigramInfo trigram="NTP" x={173} y={115.5} status={HealthStatusEnum.HEALTHY} />

        {/* Baie server */}
        <SquareInfo
          x={75}
          y={164}
          name={formatMessage({ id: `${commonHolopticsI18nPath}.baieServer` })}
          isConnected
          isPresent
          icon={IcServer}
          status={HealthStatusEnum.HEALTHY}
        />
        <TrigramInfo trigram="API" x={123} y={177.5} status={HealthStatusEnum.HEALTHY} />
        <TrigramInfo trigram="TRK" x={173} y={177.5} status={HealthStatusEnum.HEALTHY} />
        <TrigramInfo trigram="SPH" x={223} y={177.5} status={HealthStatusEnum.HEALTHY} />

        {/* Web relay */}
        <SquareInfo
          x={75}
          y={294}
          name={formatMessage({ id: `${commonHolopticsI18nPath}.webrelay` })}
          isConnected
          isPresent
          icon={IcWebRelay}
          status={HealthStatusEnum.HEALTHY}
        />

        {/* Streamer GC */}
        <SquareInfo
          x={141}
          y={366}
          name={formatMessage(
            { id: `${commonHolopticsI18nPath}.streamer` },
            { name: formatMessage({ id: `${commonHolopticsI18nPath}.gc` }) },
          )}
          isConnected
          isPresent
          icon={IcServers}
          status={HealthStatusEnum.HEALTHY}
        />
        <Gate x={144} y={300.5} isConnected onClick={() => console.log('interact gc gate')} />
        <path
          d="M161 333V362.5"
          stroke={colors.neutral['200'].value}
          strokeWidth="2"
          strokeLinejoin="bevel"
          //display={isPresent ? 'block' : 'none'}
        />

        {/* Streamer PC */}
        <SquareInfo
          x={189}
          y={366}
          name={formatMessage(
            { id: `${commonHolopticsI18nPath}.streamer` },
            { name: formatMessage({ id: `${commonHolopticsI18nPath}.pc` }) },
          )}
          isConnected
          isPresent
          icon={IcServers}
          status={HealthStatusEnum.HEALTHY}
        />
        <Gate x={192} y={300.5} isConnected />
        <path
          d="M209 333V362.5"
          stroke={colors.neutral['200'].value}
          strokeWidth="2"
          strokeLinejoin="bevel"
          //display={isPresent ? 'block' : 'none'}
        />

        {/* Streamer SWIR */}
        <SquareInfo
          x={237}
          y={366}
          name={formatMessage(
            { id: `${commonHolopticsI18nPath}.streamer` },
            { name: formatMessage({ id: `${commonHolopticsI18nPath}.swir` }) },
          )}
          isConnected
          isPresent
          icon={IcServers}
          status={HealthStatusEnum.HEALTHY}
        />
        <Gate x={240} y={300.5} isConnected />
        <path
          d="M257 333V362.5"
          stroke={colors.neutral['200'].value}
          strokeWidth="2"
          strokeLinejoin="bevel"
          //display={isPresent ? 'block' : 'none'}
        />

        {/* Streamer MWIR */}
        <SquareInfo
          x={285}
          y={366}
          name={formatMessage(
            { id: `${commonHolopticsI18nPath}.streamer` },
            { name: formatMessage({ id: `${commonHolopticsI18nPath}.mwir` }) },
          )}
          isConnected
          isPresent
          icon={IcServers}
          status={HealthStatusEnum.HEALTHY}
        />
        <Gate x={288} y={300.5} isConnected />
        <path
          d="M305 333V362.5"
          stroke={colors.neutral['200'].value}
          strokeWidth="2"
          strokeLinejoin="bevel"
          //display={isPresent ? 'block' : 'none'}
        />

        {/* Air conditioning */}
        <SquareInfo
          x={333}
          y={366}
          name={formatMessage({ id: `${commonHolopticsI18nPath}.airConditioning` })}
          isConnected
          isPresent
          icon={IcAirConditioning}
          status={HealthStatusEnum.HEALTHY}
        />
        <Gate x={336} y={300.5} isConnected />
        <path
          d="M353 333V362.5"
          stroke={colors.neutral['200'].value}
          strokeWidth="2"
          strokeLinejoin="bevel"
          //display={isPresent ? 'block' : 'none'}
        />

        {/* Ventilation */}
        <SquareInfo
          x={381}
          y={366}
          name={formatMessage({ id: `${commonHolopticsI18nPath}.ventilation` })}
          isConnected
          isPresent
          icon={IcVentilation}
          status={HealthStatusEnum.HEALTHY}
        />
        <Gate x={384} y={300.5} isConnected />
        <path
          d="M401 333V362.5"
          stroke={colors.neutral['200'].value}
          strokeWidth="2"
          strokeLinejoin="bevel"
          //display={isPresent ? 'block' : 'none'}
        />
      </g>
      <defs>
        <clipPath id="clip0_17728_68870">
          <rect width="448" height="348" fill="white" transform="translate(0 83)" />
        </clipPath>
      </defs>

      {/* Other links */}
      <path
        d="M32 62.5V101C32 114.255 42.7452 125 56 125H71.5"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
      <path
        d="M32 62.5V163C32 176.255 42.7452 187 56 187H71.5"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
      <path
        d="M32 62.5V217C32 230.255 42.7452 241 56 241H137C150.255 241 161 251.745 161 265V301"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
      <path
        d="M32 62.5V217C32 230.255 42.7452 241 56 241H185C198.255 241 209 251.745 209 265V301"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
      <path
        d="M32 62.5V217C32 230.255 42.7452 241 56 241H233C246.255 241 257 251.745 257 265V301"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
      <path
        d="M32 62.5V217C32 230.255 42.7452 241 56 241H281C294.255 241 305 251.745 305 265V301"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
      <path
        d="M32 62.5V217C32 230.255 42.7452 241 56 241H329C342.255 241 353 251.745 353 265V301"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
      <path
        d="M32 62.5V217C32 230.255 42.7452 241 56 241H377C390.255 241 401 251.745 401 265V301"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
      <path
        d="M32 62.5V293C32 306.255 42.7452 317 56 317H71.5"
        stroke={colors.neutral['200'].value}
        strokeWidth="2"
        strokeLinejoin="bevel"
      />
    </svg>
  );
}
